import { GridsterItem } from 'angular-gridster2';

export type DashboardWidgetId =
  | 'kpi-subscription'
  | 'kpi-patient-billing'
  | 'kpi-storage'
  | 'birthdays'
  | 'upcoming-births'
  | 'exam-breakdown'
  | 'patient-links'
  | 'patient-invoices'
  | 'upcoming-appointments'
  | 'subscription';

// Persisted shape — never the live GridsterItem, which gridster mutates in place.
export interface SavedLayoutItem {
  id: DashboardWidgetId;
  x: number;
  y: number;
  cols: number;
  rows: number;
}

export interface DashboardGridItem extends GridsterItem {
  id: DashboardWidgetId;
}

export const DASHBOARD_GRID_COLS = 6;
export const DASHBOARD_ROW_HEIGHT = 40;
export const DASHBOARD_MARGIN = 20;
// Below this gridster stacks full-width and ignores x/cols (old 1-column collapse).
export const DASHBOARD_MOBILE_BREAKPOINT = 800;

interface WidgetDefault {
  id: DashboardWidgetId;
  cols: number;
  rows: number;
  minItemCols: number;
  minItemRows: number;
  maxItemRows?: number;
}

// Declaration order = default reading order (flowed left-to-right); appointments before invoices so RTL mirrors it right.
export const DASHBOARD_WIDGET_REGISTRY: readonly WidgetDefault[] = [
  { id: 'exam-breakdown', cols: 4, rows: 10, minItemCols: 2, minItemRows: 4 },
  { id: 'patient-links', cols: 2, rows: 10, minItemCols: 2, minItemRows: 4 },
  // Banner height is content-determined, so vertical resize is meaningless.
  { id: 'birthdays', cols: 6, rows: 2, minItemCols: 2, minItemRows: 2, maxItemRows: 2 },
  { id: 'upcoming-births', cols: 6, rows: 2, minItemCols: 2, minItemRows: 2, maxItemRows: 2 },
  { id: 'upcoming-appointments', cols: 3, rows: 6, minItemCols: 2, minItemRows: 4 },
  { id: 'patient-invoices', cols: 3, rows: 6, minItemCols: 2, minItemRows: 4 },
  { id: 'kpi-subscription', cols: 2, rows: 5, minItemCols: 2, minItemRows: 3 },
  { id: 'kpi-patient-billing', cols: 2, rows: 5, minItemCols: 2, minItemRows: 3 },
  { id: 'kpi-storage', cols: 2, rows: 5, minItemCols: 2, minItemRows: 3 },
  { id: 'subscription', cols: 6, rows: 11, minItemCols: 3, minItemRows: 4 },
];

const REGISTRY_BY_ID = new Map(DASHBOARD_WIDGET_REGISTRY.map((widget) => [widget.id, widget]));

function isDashboardWidgetId(value: string): value is DashboardWidgetId {
  return REGISTRY_BY_ID.has(value as DashboardWidgetId);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function clampCols(widget: WidgetDefault, cols: number): number {
  return clamp(cols, widget.minItemCols, DASHBOARD_GRID_COLS);
}

function clampRows(widget: WidgetDefault, rows: number): number {
  return clamp(rows, widget.minItemRows, widget.maxItemRows ?? Number.MAX_SAFE_INTEGER);
}

function toGridItem(
  widget: WidgetDefault,
  placement: Omit<SavedLayoutItem, 'id'>
): DashboardGridItem {
  const cols = clampCols(widget, placement.cols);
  return {
    id: widget.id,
    x: clamp(placement.x, 0, DASHBOARD_GRID_COLS - cols),
    y: Math.max(placement.y, 0),
    cols,
    rows: clampRows(widget, placement.rows),
    minItemCols: widget.minItemCols,
    minItemRows: widget.minItemRows,
    ...(widget.maxItemRows === undefined ? {} : { maxItemRows: widget.maxItemRows }),
  };
}

// Flows widgets left-to-right, wrapping at grid width, so a permission-hidden widget closes up rather than leaving a hole.
export function defaultDashboardLayout(
  visibleIds: readonly DashboardWidgetId[]
): DashboardGridItem[] {
  const items: DashboardGridItem[] = [];
  let cursorX = 0;
  let rowY = 0;
  let rowHeight = 0;

  for (const widget of DASHBOARD_WIDGET_REGISTRY) {
    if (!visibleIds.includes(widget.id)) {
      continue;
    }
    const cols = clampCols(widget, widget.cols);
    if (cursorX + cols > DASHBOARD_GRID_COLS) {
      cursorX = 0;
      rowY += rowHeight;
      rowHeight = 0;
    }
    const rows = clampRows(widget, widget.rows);
    items.push(toGridItem(widget, { x: cursorX, y: rowY, cols, rows }));
    cursorX += cols;
    rowHeight = Math.max(rowHeight, rows);
  }
  return items;
}

// Saved positions win; unknown ids dropped, newly-added widgets land below at registry size.
export function mergeDashboardLayout(
  saved: readonly SavedLayoutItem[] | null,
  visibleIds: readonly DashboardWidgetId[]
): DashboardGridItem[] {
  if (!saved?.length) {
    return defaultDashboardLayout(visibleIds);
  }

  const savedById = new Map<DashboardWidgetId, SavedLayoutItem>();
  for (const item of saved) {
    if (isDashboardWidgetId(item.id)) {
      savedById.set(item.id, item);
    }
  }

  const merged: DashboardGridItem[] = [];
  for (const widget of DASHBOARD_WIDGET_REGISTRY) {
    if (!visibleIds.includes(widget.id)) {
      continue;
    }
    const savedItem = savedById.get(widget.id);
    if (savedItem) {
      merged.push(toGridItem(widget, savedItem));
    } else {
      const nextY = merged.reduce((bottom, item) => Math.max(bottom, item.y + item.rows), 0);
      merged.push(toGridItem(widget, { x: 0, y: nextY, cols: widget.cols, rows: widget.rows }));
    }
  }
  return merged;
}

// Strips gridster's mutations to the persisted shape; also the edit-mode snapshot's deep clone.
export function serializeDashboardLayout(items: readonly DashboardGridItem[]): SavedLayoutItem[] {
  return items.map((item) => ({
    id: item.id,
    x: item.x,
    y: item.y,
    cols: item.cols,
    rows: item.rows,
  }));
}
